import { useMemo, useRef, useState } from "react";
import { rotateCutout, rotatePoints } from "@/components/SlabLayoutBoard";
import { photoHref } from "@/lib/api";

// the slab photo is cropped to the sheet with zoom + offset, same as the layout board
const photoBox = (s) => {
  const zoom = Math.max(0.1, Number(s.photo_zoom) || 1);
  return {
    x: Number(s.photo_x) || 0,
    y: Number(s.photo_y) || 0,
    width: (Number(s.length_in) || 1) * zoom,
    height: (Number(s.width_in) || 1) * zoom,
  };
};

const r2 = (n) => Math.round(n * 100) / 100;
const areaKey = (p) => p.area_id || p.area_name || "";
const bboxAt = (p, x, y) => {
  const g = rotatePoints(p.points, p.rot || 0);
  return { x0: x, y0: y, x1: x + g.w, y1: y + g.h };
};
const bounds = (p, dx = 0, dy = 0) => {
  const g = rotatePoints(p.points, p.rot || 0);
  const X = (Number(p.x) || 0) + dx;
  const Y = (Number(p.y) || 0) + dy;
  return { x0: X, x1: X + g.w, y0: Y, y1: Y + g.h };
};
const MAX_GAP = 30;

const polyContains = (pts, [x, y]) => {
  let hit = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i];
    const [xj, yj] = pts[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
};
const globalPts = (p) => {
  const g = rotatePoints(p.points, p.rot || 0).pts;
  return g.map(([x, y]) => [x + (Number(p.x) || 0), y + (Number(p.y) || 0)]);
};
const isUpright = (p) =>
  ["backsplash", "waterfall", "mitre"].includes(p.panel_kind) ||
  (p.orientation === "vertical" && (p.stand === "backsplash" || p.stand === "waterfall"));
const norm = ([x, y]) => {
  const l = Math.hypot(x, y) || 1;
  return [x / l, y / l];
};
const edgeLenOf = (pts, i) => Math.hypot(pts[(i + 1) % pts.length][0] - pts[i][0], pts[(i + 1) % pts.length][1] - pts[i][1]);

// pick the counter edge an upright panel belongs to: its `attach` link, else the
// nearest same-length edge of a flat piece in the same area
const findEdge = (panel, all) => {
  const flats = all.filter((q) => q.id !== panel.id && areaKey(q) === areaKey(panel) && !isUpright(q) && (q.points || []).length > 2);
  if (panel.attach?.piece_id) {
    const parent = flats.find((q) => q.id === panel.attach.piece_id);
    if (parent && parent.points[panel.attach.edge]) return { parent, edge: panel.attach.edge };
  }
  // auto panels are named `<parent>-<kind>-<edge>`
  const m = /^(.*)-(backsplash|waterfall|mitre)-(\d+)$/.exec(panel.id || "");
  if (m) {
    const parent = flats.find((q) => q.id === m[1]);
    if (parent && parent.points[Number(m[3])]) return { parent, edge: Number(m[3]) };
  }
  const L = edgeLenOf(panel.points, 0);
  const pb = bounds(panel);
  const cx = (pb.x0 + pb.x1) / 2;
  const cy = (pb.y0 + pb.y1) / 2;
  let best = null;
  flats.forEach((q) => {
    const gp = globalPts(q);
    gp.forEach((_, i) => {
      if (Math.abs(edgeLenOf(gp, i) - L) > 1) return;
      const mx = (gp[i][0] + gp[(i + 1) % gp.length][0]) / 2;
      const my = (gp[i][1] + gp[(i + 1) % gp.length][1]) / 2;
      const d = Math.hypot(mx - cx, my - cy);
      if (d < 60 && (!best || d < best.d)) best = { parent: q, edge: i, d };
    });
  });
  return best;
};

// lay an upright panel flat against its counter edge, on the outside, like a fold-down
const foldPanel = (panel, all) => {
  const hit = findEdge(panel, all);
  if (!hit) return panel;
  const gp = globalPts(hit.parent);
  const A = gp[hit.edge];
  const B = gp[(hit.edge + 1) % gp.length];
  const [ux, uy] = norm([B[0] - A[0], B[1] - A[1]]);
  let n = [uy, -ux];
  const mid = [(A[0] + B[0]) / 2 + n[0] * 0.5, (A[1] + B[1]) / 2 + n[1] * 0.5];
  if (polyContains(gp, mid)) n = [-uy, ux];
  const lp = panel.points;
  const [lux, luy] = norm([lp[1][0] - lp[0][0], lp[1][1] - lp[0][1]]);
  let m = [luy, -lux];
  const toP2 = [lp[2][0] - lp[0][0], lp[2][1] - lp[0][1]];
  if (m[0] * toP2[0] + m[1] * toP2[1] < 0) m = [-luy, lux];
  const base = ((Math.atan2(n[1], n[0]) - Math.atan2(m[1], m[0])) * 180) / Math.PI;
  const pick = [base, base + 180]
    .map((deg) => {
      const rot = ((Math.round(deg * 10) / 10) % 360 + 360) % 360;
      const rp = rotatePoints(lp, rot).pts;
      const [dx, dy] = norm([rp[1][0] - rp[0][0], rp[1][1] - rp[0][1]]);
      let bn = [dy, -dx];
      const t2 = [rp[2][0] - rp[0][0], rp[2][1] - rp[0][1]];
      if (bn[0] * t2[0] + bn[1] * t2[1] < 0) bn = [-dy, dx];
      return { rot, rp, dx, dy, ok: bn[0] * n[0] + bn[1] * n[1] > 0.9 };
    })
    .find((c) => c.ok);
  if (!pick) return panel;
  const S = pick.dx * (B[0] - A[0]) + pick.dy * (B[1] - A[1]) >= 0 ? A : B;
  return {
    ...panel,
    rot: pick.rot,
    x: r2(S[0] - pick.rp[0][0]),
    y: r2(S[1] - pick.rp[0][1]),
    folded: true,
  };
};

// the parent counter edge an existing upright panel belongs to, as `parentId:edge`
const splashEdgeKey = (u) => {
  if (u.attach?.piece_id) return `${u.attach.piece_id}:${Number(u.attach.edge) || 0}`;
  const m = /^(.*)-(backsplash|waterfall|mitre)-(\d+)$/.exec(u.id || "");
  return m ? `${m[1]}:${Number(m[3])}` : null;
};

// A backsplash drawn as an edge height (not a separate panel / upright piece) must still
// show in 2D, folded down against its edge like the derived panels — so 2D stays in sync
// with 3D/CAD where the same edge backsplash now stands.
const withEdgeSplashes = (list) => {
  const taken = new Set();
  list.forEach((u) => {
    if (isUpright(u)) {
      const k = splashEdgeKey(u);
      if (k) taken.add(k);
    }
  });
  const extra = [];
  list.forEach((p) => {
    if (isUpright(p) || (p.points || []).length <= 2) return;
    (p.edges || []).forEach((e, i) => {
      const H = Number(e?.backsplash_height_in) || 0;
      if (H <= 0 || taken.has(`${p.id}:${i}`)) return;
      const a = p.points[i];
      const b = p.points[(i + 1) % p.points.length];
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      extra.push({
        id: `${p.id}-backsplash-${i}`,
        area_id: p.area_id,
        area_name: p.area_name,
        panel_kind: "backsplash",
        attach: { piece_id: p.id, edge: i },
        points: [
          [0, 0],
          [L, 0],
          [L, H],
          [0, H],
        ],
        x: 0,
        y: 0,
      });
    });
  });
  return extra.length ? [...list, ...extra] : list;
};

// preview-only shift that pulls every piece flush against its nearest neighbour
const tighten = (list) => {
  const sh = {};
  list.forEach((p) => {
    sh[p.id] = { dx: 0, dy: 0 };
  });
  if (list.length < 2) return sh;
  const bb = (p) => bounds(p, sh[p.id].dx, sh[p.id].dy);
  const size = (p) => {
    const b = bb(p);
    return (b.x1 - b.x0) * (b.y1 - b.y0);
  };
  const sorted = [...list].sort((a, b) => size(b) - size(a));
  const placed = [sorted[0]];
  sorted.slice(1).forEach((p) => {
    const me = bb(p);
    let best = null;
    placed.forEach((o) => {
      const b = bb(o);
      const ovY = Math.min(me.y1, b.y1) - Math.max(me.y0, b.y0);
      const ovX = Math.min(me.x1, b.x1) - Math.max(me.x0, b.x0);
      if (ovX > 0.5 && ovY > 0.5) return;
      const consider = (g, axis) => {
        if (Math.abs(g) <= MAX_GAP && (!best || Math.abs(g) < Math.abs(best.g))) best = { g, axis };
      };
      if (ovY > 0.5) {
        consider(b.x0 - me.x1, "x");
        consider(b.x1 - me.x0, "x");
      }
      if (ovX > 0.5) {
        consider(b.y0 - me.y1, "y");
        consider(b.y1 - me.y0, "y");
      }
    });
    if (best) sh[p.id][best.axis === "x" ? "dx" : "dy"] += best.g;
    placed.push(p);
  });
  return sh;
};

/** Pieces drawn where they will be installed, filled with the slab photo from
 *  the exact spot each piece is cut from — so the vein reads through the seam.
 *  With `onMove` the pieces can be dragged; their edges snap flush to (and line up
 *  with) neighbouring pieces in the same area. `tight` closes drawing gaps for the
 *  preview only; `seams` highlights every shared edge. */
export const SeamPreview = ({
  pieces: rawPieces = [],
  slabs = [],
  placements = {},
  labels = true,
  selected = null,
  onSelect,
  light = false,
  photoOf = (s) => photoHref(s.photo_url),
  onMove,
  onMoveStart,
  onMoveEnd,
  onContextMenu,
  tight = false,
  seams = false,
  fold = true,
  onSeamClick,
  onMarquee,
  lens = null,
  snap = true,
  fill = false,
  free = false,
  veinWrap = true,
  seamBadge = false,
}) => {
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  // upright splashes / waterfalls are shown folded down against their counter edge
  const pieces = useMemo(() => {
    if (!fold) return rawPieces;
    const list = withEdgeSplashes(rawPieces);
    return list.map((p) => (isUpright(p) && (p.points || []).length > 2 ? foldPanel(p, list) : p));
  }, [rawPieces, fold]);

  const view = useMemo(() => {
    const drawn = pieces.filter((p) => (p.points || []).length > 2);
    if (!drawn.length) return null;
    // group pieces by area so separate rooms don't pile on the same origin
    const order = [];
    const groups = {};
    drawn.forEach((p) => {
      const k = areaKey(p);
      if (!groups[k]) {
        groups[k] = [];
        order.push(k);
      }
      groups[k].push(p);
    });
    const shift = {};
    const offsets = {};
    const seamLines = [];
    const GAP = 12;
    let runX = 0;
    let gx0 = Infinity;
    let gy0 = Infinity;
    let gx1 = -Infinity;
    let gy1 = -Infinity;
    order.forEach((k) => {
      const list = groups[k];
      const sh = tight ? tighten(list) : Object.fromEntries(list.map((p) => [p.id, { dx: 0, dy: 0 }]));
      let amin = Infinity;
      let amax = -Infinity;
      let ymin = Infinity;
      let ymax = -Infinity;
      const bbs = {};
      list.forEach((p) => {
        shift[p.id] = sh[p.id];
        const b = bounds(p, sh[p.id].dx, sh[p.id].dy);
        bbs[p.id] = b;
        amin = Math.min(amin, b.x0);
        amax = Math.max(amax, b.x1);
        ymin = Math.min(ymin, b.y0);
        ymax = Math.max(ymax, b.y1);
      });
      const off = runX - amin;
      list.forEach((p) => {
        offsets[p.id] = off;
      });
      // shared edges between touching neighbours
      for (let i = 0; i < list.length; i += 1) {
        for (let j = i + 1; j < list.length; j += 1) {
          const a = bbs[list[i].id];
          const b = bbs[list[j].id];
          const ovY = Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0);
          const ovX = Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0);
          if (ovY > 1 && Math.abs(a.x1 - b.x0) < 0.6) seamLines.push([a.x1 + off, Math.max(a.y0, b.y0), a.x1 + off, Math.min(a.y1, b.y1)]);
          else if (ovY > 1 && Math.abs(b.x1 - a.x0) < 0.6) seamLines.push([b.x1 + off, Math.max(a.y0, b.y0), b.x1 + off, Math.min(a.y1, b.y1)]);
          if (ovX > 1 && Math.abs(a.y1 - b.y0) < 0.6) seamLines.push([Math.max(a.x0, b.x0) + off, a.y1, Math.min(a.x1, b.x1) + off, a.y1]);
          else if (ovX > 1 && Math.abs(b.y1 - a.y0) < 0.6) seamLines.push([Math.max(a.x0, b.x0) + off, b.y1, Math.min(a.x1, b.x1) + off, b.y1]);
        }
      }
      gx0 = Math.min(gx0, amin + off);
      gx1 = Math.max(gx1, amax + off);
      gy0 = Math.min(gy0, ymin);
      gy1 = Math.max(gy1, ymax);
      runX += amax - amin + GAP;
    });
    const pad = 6;
    return {
      offsets,
      shift,
      seamLines,
      box: `${gx0 - pad} ${gy0 - pad} ${gx1 - gx0 + pad * 2} ${gy1 - gy0 + pad * 2}`,
      scale: (gx1 - gx0 + pad * 2) / 100,
    };
  }, [pieces, tight]);

  // hold the framing still while a piece is being dragged so the sheet doesn't jump
  const heldRef = useRef(view);
  if (!dragging) heldRef.current = view;
  const v = heldRef.current || view;
  const box = lens ? `${lens.x - lens.size / 2} ${lens.y - lens.size / 2} ${lens.size} ${lens.size}` : v?.box;

  const toSvg = (e) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const m = svg.getScreenCTM();
    if (!m) return null;
    const loc = pt.matrixTransform(m.inverse());
    return [loc.x, loc.y];
  };

  const startDrag = (e, p) => {
    if (!onMove || p.folded || e.button === 1 || e.button === 2) return;
    e.stopPropagation();
    e.preventDefault();
    const at = toSvg(e);
    if (!at) return;
    svgRef.current?.setPointerCapture?.(e.pointerId);
    dragRef.current = { piece: p, at, ox: Number(p.x) || 0, oy: Number(p.y) || 0, moved: false };
    setDragging(true);
    onSelect?.(p.id, e.shiftKey);
  };

  const moveDrag = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const at = toSvg(e);
    if (!at) return;
    const lo = free ? -Infinity : 0;
    let nx = Math.max(lo, d.ox + at[0] - d.at[0]);
    let ny = Math.max(lo, d.oy + at[1] - d.at[1]);
    // snap flush / in line with the neighbours in the same area
    const me = bboxAt(d.piece, nx, ny);
    const thr = Math.max(1.5, (v?.scale || 1) * 1.2);
    let bestX = null;
    let bestY = null;
    if (snap) pieces.forEach((o) => {
      if (o.id === d.piece.id || areaKey(o) !== areaKey(d.piece) || (o.points || []).length < 3) return;
      const b = bboxAt(o, Number(o.x) || 0, Number(o.y) || 0);
      [[me.x1, b.x0], [me.x0, b.x1], [me.x0, b.x0], [me.x1, b.x1]].forEach(([a, t]) => {
        const dlt = t - a;
        if (Math.abs(dlt) < thr && (bestX === null || Math.abs(dlt) < Math.abs(bestX))) bestX = dlt;
      });
      [[me.y1, b.y0], [me.y0, b.y1], [me.y0, b.y0], [me.y1, b.y1]].forEach(([a, t]) => {
        const dlt = t - a;
        if (Math.abs(dlt) < thr && (bestY === null || Math.abs(dlt) < Math.abs(bestY))) bestY = dlt;
      });
    });
    const grid = snap ? 4 : 100;
    nx = bestX !== null ? nx + bestX : Math.round(nx * grid) / grid;
    ny = bestY !== null ? ny + bestY : Math.round(ny * grid) / grid;
    // pieces may hang past the slab edge (to cut a seam) — keep at least 6" on the slab
    nx = Math.max(free ? lo : Math.min(0, 6 - (me.x1 - me.x0)), nx);
    ny = Math.max(free ? lo : Math.min(0, 6 - (me.y1 - me.y0)), ny);
    if (!d.moved) onMoveStart?.(d.piece.id);
    d.moved = true;
    onMove(d.piece.id, r2(nx), r2(ny), d.piece);
  };

  const endDrag = () => {
    const d = dragRef.current;
    if (d) {
      dragRef.current = null;
      setDragging(false);
      if (d.moved) onMoveEnd?.(d.piece.id);
    }
    const m = marqueeRef.current;
    if (m) {
      marqueeRef.current = null;
      setMarquee(null);
      const x0 = Math.min(m.a[0], m.b[0]), x1 = Math.max(m.a[0], m.b[0]);
      const y0 = Math.min(m.a[1], m.b[1]), y1 = Math.max(m.a[1], m.b[1]);
      if (x1 - x0 < 0.5 && y1 - y0 < 0.5) {
        onMarquee?.([], m.shift, true);
        return;
      }
      const ids = pieces
        .filter((p) => {
          const b = bboxAt(p, Number(p.x) || 0, Number(p.y) || 0);
          const off = (v?.offsets?.[p.id] || 0) + (sh(p.id).dx || 0);
          const oy = sh(p.id).dy || 0;
          return b.x0 + off < x1 && b.x1 + off > x0 && b.y0 + oy < y1 && b.y1 + oy > y0;
        })
        .map((p) => p.id);
      onMarquee?.(ids, m.shift, false);
    }
  };

  // marquee: drag on empty canvas to box-select (free canvas only)
  const marqueeRef = useRef(null);
  const [marquee, setMarquee] = useState(null);
  const startMarquee = (e) => {
    if (!onMarquee || e.button !== 0 || dragRef.current) return;
    const at = toSvg(e);
    if (!at) return;
    e.preventDefault();
    svgRef.current?.setPointerCapture?.(e.pointerId);
    marqueeRef.current = { a: at, b: at, shift: e.shiftKey };
    setMarquee({ a: at, b: at });
  };
  const moveMarquee = (e) => {
    const m = marqueeRef.current;
    if (!m) return;
    const at = toSvg(e);
    if (!at) return;
    m.b = at;
    setMarquee({ a: m.a, b: at });
  };

  if (!v) return null;
  const fs = Math.min(2.2, Math.max(0.9, v.scale * 1.15));

  const baseTexture = (p) => {
    const pl = placements[p.id];
    if (!pl) return null;
    const slab = slabs[pl.slab];
    const href = slab ? photoOf(slab) : "";
    if (!href) return null;
    const gi = rotatePoints(p.points, p.rot || 0);
    const gs = rotatePoints(p.points, pl.rot || 0);
    const ex = (Number(p.x) || 0) + gi.pts[0][0];
    const ey = (Number(p.y) || 0) + gi.pts[0][1];
    const fx = (Number(pl.x) || 0) + gs.pts[0][0];
    const fy = (Number(pl.y) || 0) + gs.pts[0][1];
    const theta = (Number(p.rot) || 0) - (Number(pl.rot) || 0);
    // flipped veins: mirror the photo about the piece's own centre (in slab space)
    let flip = "";
    if (pl.flip_x || pl.flip_y) {
      const xs = gs.pts.map((q) => q[0]);
      const ys = gs.pts.map((q) => q[1]);
      const cx = (Number(pl.x) || 0) + (Math.min(...xs) + Math.max(...xs)) / 2;
      const cy = (Number(pl.y) || 0) + (Math.min(...ys) + Math.max(...ys)) / 2;
      flip = ` translate(${cx} ${cy}) scale(${pl.flip_x ? -1 : 1},${pl.flip_y ? -1 : 1}) translate(${-cx} ${-cy})`;
    }
    return {
      slab,
      href,
      transform: `translate(${ex} ${ey}) rotate(${theta})${flip} translate(${-fx} ${-fy})`,
      // a book-matched (mirrored) slab shows its photo flipped, same as the layout board
      mirror: slab.mirror ? `translate(${Number(slab.length_in) || 0},0) scale(-1,1)` : undefined,
    };
  };

  // Vein wrap: a folded waterfall / mitre panel borrows its parent counter's slab mapping, so the
  // stone veins flow unbroken down the waterfall and across the mitred corner in the preview.
  const texture = (p) => {
    if (veinWrap && p.folded) {
      const hit = findEdge(p, pieces);
      if (hit?.parent && hit.parent.id !== p.id && placements[hit.parent.id]) {
        const pt = baseTexture(hit.parent);
        if (pt) return pt;
      }
    }
    return baseTexture(p);
  };

  const sh = (id) => v.shift?.[id] || { dx: 0, dy: 0 };

  return (
    <svg
      ref={svgRef}
      viewBox={box}
      preserveAspectRatio="xMidYMid meet"
      className={`w-full ${fill ? "h-full" : ""} ${dragging ? "cursor-grabbing" : ""}`}
      data-snap={snap ? "1" : "0"}
      style={onMove ? { touchAction: "none" } : undefined}
      data-testid={lens ? "seam-lens-svg" : "seam-preview-svg"}
      data-dragging={dragging ? "1" : "0"}
      data-tight={tight ? "1" : "0"}
      onPointerMove={onMove ? (e) => { moveDrag(e); moveMarquee(e); } : undefined}
      onPointerUp={onMove ? endDrag : undefined}
      onPointerCancel={onMove ? endDrag : undefined}
      onPointerDown={onMarquee ? startMarquee : undefined}
    >
      <defs>
        <pattern id="seam-nophoto" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="4" height="4" fill={light ? "#e7e5e4" : "#52525b"} />
          <line x1="0" y1="0" x2="0" y2="4" stroke={light ? "#a8a29e" : "#a1a1aa"} strokeWidth="1.2" />
        </pattern>
      </defs>
      {pieces
        .filter((p) => (p.points || []).length > 2)
        .map((p) => {
          const gi = rotatePoints(p.points, p.rot || 0);
          const px = Number(p.x) || 0;
          const py = Number(p.y) || 0;
          const pts = gi.pts.map(([x, y]) => `${x + px},${y + py}`).join(" ");
          const tex = texture(p);
          const sel = Array.isArray(selected) ? selected.includes(p.id) : selected === p.id;
          const s = sh(p.id);
          return (
            <g
              key={p.id}
              data-testid={`seam-piece-${p.id}`}
              data-selected={sel ? "1" : undefined}
              transform={`translate(${(v.offsets?.[p.id] || 0) + s.dx} ${s.dy})`}
              onClick={onMove ? undefined : (e) => onSelect?.(p.id, e.shiftKey)}
              onContextMenu={
                onContextMenu
                  ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onSelect?.(p.id);
                      onContextMenu(p, e.clientX, e.clientY);
                    }
                  : undefined
              }
              onPointerDown={onMove ? (e) => startDrag(e, p) : undefined}
              data-folded={p.folded ? "1" : undefined}
              className={onMove && !p.folded ? "cursor-grab" : onSelect ? "cursor-pointer" : ""}
            >
              <clipPath id={`seam-body-${p.id}`}>
                <polygon points={pts} />
              </clipPath>
              {tex ? (
                <>
                  <clipPath id={`seam-clip-${p.id}`}>
                    <polygon points={pts} />
                  </clipPath>
                  <g clipPath={`url(#seam-clip-${p.id})`}>
                    <g transform={tex.transform}>
                      <g transform={tex.mirror}>
                        <image
                          href={tex.href}
                          preserveAspectRatio="xMidYMid slice"
                          {...photoBox(tex.slab)}
                        />
                      </g>
                    </g>
                  </g>
                </>
              ) : (
                <polygon points={pts} fill="url(#seam-nophoto)" data-testid={`seam-nophoto-${p.id}`} />
              )}
              {(p.cutouts || []).map((c, n) => {
                return (
                  <g key={c.id || n} clipPath={`url(#seam-body-${p.id})`}>
                    <polygon
                      data-testid={`seam-cutout-${c.id || n}`}
                      transform={`translate(${px} ${py})`}
                      points={rotateCutout(c, p.points, p.rot || 0)}
                      fill="rgba(9,9,11,0.7)"
                      stroke="#a1a1aa"
                      strokeWidth={fs * 0.06}
                    />
                  </g>
                );
              })}
              {/* a sink straddling the seam: draw the neighbour's portion clipped to this piece */}
              {(p.rot || 0) === 0 &&
                pieces
                  .filter((q) => q.id !== p.id && areaKey(q) === areaKey(p) && (q.rot || 0) === 0)
                  .flatMap((q) =>
                    (q.cutouts || []).map((c, n) => {
                      const sq = sh(q.id);
                      const dx = (Number(q.x) || 0) - px + sq.dx - s.dx;
                      const dy = (Number(q.y) || 0) - py + sq.dy - s.dy;
                      const x0 = (Number(c.x) || 0) + dx;
                      const y0 = (Number(c.y) || 0) + dy;
                      const w = Number(c.w) || 6;
                      const h = Number(c.h) || 6;
                      if (x0 + w <= 0 || y0 + h <= 0 || x0 >= gi.w || y0 >= gi.h) return null;
                      return (
                        <polygon
                          key={`${q.id}-${c.id || n}`}
                          data-testid={`seam-cutout-spill-${p.id}-${c.id || n}`}
                          transform={`translate(${px} ${py})`}
                          points={rotateCutout({ ...c, x: x0, y: y0 }, q.points, 0)}
                          fill="rgba(9,9,11,0.7)"
                          stroke="#a1a1aa"
                          strokeWidth={fs * 0.06}
                          clipPath={`url(#seam-body-${p.id})`}
                        />
                      );
                    }),
                  )}
              <polygon
                points={pts}
                fill="none"
                stroke={sel ? "#22d3ee" : light ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.35)"}
                strokeWidth={fs * (sel ? 0.1 : 0.05)}
              />
              {labels && (
                <text
                  x={px + gi.w / 2}
                  y={py + gi.h / 2}
                  textAnchor="middle"
                  fill="#fafafa"
                  stroke="rgba(0,0,0,0.65)"
                  strokeWidth={fs * 0.14}
                  paintOrder="stroke"
                  fontSize={fs}
                  fontWeight="bold"
                  className="pointer-events-none select-none"
                >
                  {p.label}
                </text>
              )}
            </g>
          );
        })}
      {seams &&
        (v.seamLines || []).map(([x1, y1, x2, y2], i) => (
          <g key={i}>
            <line
              data-testid={`seam-line-${i}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#f59e0b"
              strokeWidth={fs * 0.12}
              strokeDasharray={`${fs * 0.5} ${fs * 0.35}`}
              className="pointer-events-none"
            />
            {onSeamClick && (
              <line
                data-testid={`seam-line-hit-${i}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="transparent"
                strokeWidth={fs * 1.2}
                className="cursor-zoom-in"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onSeamClick({ x: (x1 + x2) / 2, y: (y1 + y2) / 2, len: Math.hypot(x2 - x1, y2 - y1), i });
                }}
              />
            )}
            {seamBadge && (
              <g
                data-testid={`seam-linked-badge-${i}`}
                transform={`translate(${(x1 + x2) / 2} ${(y1 + y2) / 2})`}
                className="pointer-events-none select-none"
              >
                <rect
                  x={-fs * 1.95}
                  y={-fs * 0.78}
                  width={fs * 3.9}
                  height={fs * 1.56}
                  rx={fs * 0.42}
                  fill="#f59e0b"
                  stroke="rgba(0,0,0,0.3)"
                  strokeWidth={fs * 0.04}
                />
                <text
                  x={0}
                  y={fs * 0.4}
                  textAnchor="middle"
                  fontSize={fs * 0.92}
                  fontWeight="bold"
                  fill="#1c1917"
                >
                  Linked
                </text>
              </g>
            )}
          </g>
        ))}
      {lens && (
        <>
          <line x1={lens.x - lens.size} y1={lens.y} x2={lens.x + lens.size} y2={lens.y} stroke="rgba(34,211,238,0.5)" strokeWidth={lens.size / 400} className="pointer-events-none" />
          <line x1={lens.x} y1={lens.y - lens.size} x2={lens.x} y2={lens.y + lens.size} stroke="rgba(34,211,238,0.5)" strokeWidth={lens.size / 400} className="pointer-events-none" />
        </>
      )}
      {marquee && (
        <rect
          data-testid="seam-marquee"
          x={Math.min(marquee.a[0], marquee.b[0])}
          y={Math.min(marquee.a[1], marquee.b[1])}
          width={Math.abs(marquee.b[0] - marquee.a[0])}
          height={Math.abs(marquee.b[1] - marquee.a[1])}
          fill="rgba(34,211,238,0.12)"
          stroke="#22d3ee"
          strokeWidth={fs * 0.08}
          strokeDasharray={`${fs * 0.4} ${fs * 0.25}`}
          className="pointer-events-none"
        />
      )}
    </svg>
  );
};
